import type { Translate } from "@similarweb/i18n-provider";

import type { ILocationDetailsToPresent } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { ISearchEngineDetails } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import {
    availableSearchEngineCompany,
    ESearchEngineDevice,
} from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { removeProtocolAndWww } from "app/pages/digital-marketing/utils/url";
import CountryService from "app/services/CountryService";
import type { WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import { getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";

import { SEARCH_CONSOLE_DOMAIN_PREFIX } from "./constants";
import type { GscSite, GscSyncInput, GscWizardSyncStatus, MarketSearchEngine, SavedGscMarket } from "./types";

/**
 * Extracts a bare domain from a GSC property identifier.
 * Handles both URL-prefix properties (`https://www.example.com/`)
 * and Domain properties (`sc-domain:example.com`).
 *
 * @example
 * extractGscDomain("https://www.example.com/")     => "example.com"
 * extractGscDomain("sc-domain:example.com")        => "example.com"
 * extractGscDomain("http://blog.example.com/path") => "blog.example.com"
 */
export const extractGscDomain = (siteProperty: string): string => {
    const withoutScDomain = siteProperty.replace(SEARCH_CONSOLE_DOMAIN_PREFIX, "");
    const withoutProtocolAndWww = removeProtocolAndWww(withoutScDomain);
    return withoutProtocolAndWww.split("/")[0];
};

/**
 * Formatted label for the WebsitePicker dropdown.
 * Uses a translate function so "(domain property)" is localizable.
 */
export const formatGscDisplayText = (siteProperty: string, translate: Translate): string => {
    if (siteProperty.startsWith(SEARCH_CONSOLE_DOMAIN_PREFIX)) {
        const domain = siteProperty.replace(SEARCH_CONSOLE_DOMAIN_PREFIX, "");
        return translate("rankTracker.gsc.websitePicker.domainProperty", {
            domain,
        });
    }
    return siteProperty;
};

/**
 * Derives the campaign site value from a GSC property.
 * Strips protocol, www prefix, and trailing slashes. Preserves path.
 *
 * "sc-domain:example.com"          => "example.com"
 * "https://example.com/"           => "example.com"
 * "https://www.example.com/"       => "example.com"
 * "https://www.example.com/blog"   => "example.com/blog"
 * "https://blog.example.com/"      => "blog.example.com"
 * "https://api.example.com/v2"     => "api.example.com/v2"
 */
export const deriveCampaignSite = (siteProperty: string): string => {
    const withoutScDomain = siteProperty.replace(SEARCH_CONSOLE_DOMAIN_PREFIX, "");
    return removeProtocolAndWww(withoutScDomain).replace(/\/+$/, "");
};

/**
 * Derives the tracking scope for campaign creation.
 *
 * Reuses `getUrlComponents` (the same URL parser used in the Step 1 autocomplete)
 * and follows the exact same decision tree as Step 1's `handleGetListItems`,
 * ensuring consistent tracking scope regardless of entry point:
 *
 * - sc-domain: properties    => "subdomain"
 * - URL with query params    => "exact"
 * - URL with folder/path     => "folder"
 * - URL without path         => "subdomain"
 *
 * Note: "domain" is intentionally never returned — the campaign save API
 * does not use it, so all bare-domain cases default to "subdomain".
 */
export const deriveTrackingScope = (siteProperty: string): WebsitePageSearchType => {
    const { queryParams, folder } = getUrlComponents(deriveCampaignSite(siteProperty));
    if (queryParams) {
        return "exact";
    }
    if (folder) {
        return "folder";
    }
    return "subdomain";
};

/**
 * Strips sc-domain: prefix, protocol, and www. to produce a comparable
 * path string. Used as a fallback when deriveCampaignSite (which preserves www)
 * fails to find a match.
 */
const normalizeSiteForMatch = (site: string): string => {
    return site.replace(new RegExp(`^(${SEARCH_CONSOLE_DOMAIN_PREFIX}|https?://)(www\\.)?`), "").replace(/\/+$/, "");
};

const isPrefixWithBoundary = (target: string, prefix: string): boolean => {
    return target.startsWith(prefix) && (target.length === prefix.length || target[prefix.length] === "/");
};

/**
 * Finds the GSC property that most closely matches the Step 1 site URL.
 *
 * Two normalizations:
 *  1. deriveCampaignSite (preserves www) — distinguishes
 *     "www.amazon.com" (URL-prefix) from "amazon.com" (sc-domain).
 *  2. normalizeSiteForMatch (strips www) — catches cross-www matches
 *     like Step 1 "amazon.com/blog" → property "https://www.amazon.com/blog".
 *
 * Priority: exact match (both normalizations) → longest prefix (both) → fallback.
 */
export const findBestMatchingSite = (
    properties: GscSite[],
    targetDomain: string,
    targetSite: string,
): GscSite | undefined => {
    const domainMatches = properties
        .filter((p) => p.domain === targetDomain)
        .sort((a, b) => {
            const aIsDomain = a.site.startsWith(SEARCH_CONSOLE_DOMAIN_PREFIX) ? 0 : 1;
            const bIsDomain = b.site.startsWith(SEARCH_CONSOLE_DOMAIN_PREFIX) ? 0 : 1;
            return aIsDomain - bIsDomain;
        });
    if (domainMatches.length === 0) {
        return undefined;
    }
    if (domainMatches.length === 1) {
        return domainMatches[0];
    }

    const normalizers = [deriveCampaignSite, normalizeSiteForMatch] as const;

    for (const normalize of normalizers) {
        const targetNorm = normalize(targetSite);
        const exact = domainMatches.find((p) => normalize(p.site) === targetNorm);
        if (exact) {
            return exact;
        }
    }

    for (const normalize of normalizers) {
        const targetNorm = normalize(targetSite);
        let bestPrefix: GscSite | undefined;
        let bestLen = 0;
        for (const p of domainMatches) {
            const propNorm = normalize(p.site);
            if (isPrefixWithBoundary(targetNorm, propNorm) && propNorm.length > bestLen) {
                bestPrefix = p;
                bestLen = propNorm.length;
            }
        }
        if (bestPrefix) {
            return bestPrefix;
        }
    }

    return domainMatches.find((p) => p.site.startsWith(SEARCH_CONSOLE_DOMAIN_PREFIX)) ?? domainMatches[0];
};

const mapDeviceToSearchEngine = (device: LocationDevice): ESearchEngineDevice => {
    if (device === "DESKTOP") {
        return ESearchEngineDevice.DESKTOP;
    }
    if (device === "MOBILE") {
        return ESearchEngineDevice.MOBILE;
    }
    return ESearchEngineDevice.COMBINED;
};

// Converts saved GSC markets into wizard-compatible search engine entries ("BOTH" splits into desktop + mobile).
export const buildSearchEngines = (
    savedMarkets: Map<number, SavedGscMarket>,
    locationsList?: ILocationDetailsToPresent[],
): MarketSearchEngine[] => {
    return [...savedMarkets.entries()].flatMap(([key, market]) => {
        const country = CountryService.getCountryByCode(market.countryCode);
        if (!country) {
            return [];
        }

        // Use the canonical English name from the backend locations API so the create-campaign
        // payload sends a backend-valid geoLocation regardless of the user's UI locale.
        // Falls back to CountryService.text only when the country is absent from the locations list.
        const canonicalText = locationsList?.find((loc) => Number(loc.id) === country.id)?.text ?? country.text;

        const baseSearchEngine: Partial<ISearchEngineDetails> = {
            company: availableSearchEngineCompany,
            language: market.language,
            location: {
                id: country.id,
                text: canonicalText,
                type: "Country",
            },
        };

        if (market.device === "BOTH") {
            return [
                {
                    key,
                    market,
                    searchEngines: [
                        {
                            ...baseSearchEngine,
                            id: `gsc-${key}-desktop`,
                            device: ESearchEngineDevice.DESKTOP,
                        },
                        {
                            ...baseSearchEngine,
                            id: `gsc-${key}-mobile`,
                            device: ESearchEngineDevice.MOBILE,
                        },
                    ],
                },
            ];
        }

        return [
            {
                key,
                market,
                searchEngines: [
                    {
                        ...baseSearchEngine,
                        id: `gsc-${key}`,
                        device: mapDeviceToSearchEngine(market.device),
                    },
                ],
            },
        ];
    });
};

export const computeTrackedKeywordsCount = (searchEnginesByMarket: MarketSearchEngine[]): number =>
    searchEnginesByMarket.reduce(
        (total, { market, searchEngines }) => total + market.keywords.length * searchEngines.length,
        0,
    );

/**
 * Pure status computation from GSC inputs — safe to call outside a state updater.
 * Reuses `buildSearchEngines` so the counts stay consistent with `syncWizardStateFromGsc`.
 */
export const computeGscSyncStatus = (input: GscSyncInput): GscWizardSyncStatus => {
    const { selectedSite, savedMarkets, hasUnsavedMarket } = input;
    const hasSelectedSite = !!selectedSite;
    const searchEnginesByMarket = hasSelectedSite ? buildSearchEngines(savedMarkets) : [];
    const trackedKeywordsCount = computeTrackedKeywordsCount(searchEnginesByMarket);

    return { hasSelectedSite, savedMarketsCount: searchEnginesByMarket.length, trackedKeywordsCount, hasUnsavedMarket };
};
